/** Vendor-only purchase-order contract. Never pass a retail order through to the browser. */
export const VENDOR_STAGES = [
  "review",
  "accepted",
  "fabricating",
  "painting",
  "installing",
  "complete",
] as const;
export type VendorStage = (typeof VENDOR_STAGES)[number];
export type VendorOrder = {
  id: string;
  number: string;
  version: string;
  title: string;
  layout: string;
  upper: string;
  lower: string;
  access: string;
  finish: string;
  finishSpecifications?: {room:string;bunk:number;finish:string;paintCode:string|null}[];
  quantity: number;
  zip: string;
  amountUsd: number;
  wholesalePending?: boolean;
  scope?: string[];
  reviewRequired?: boolean;
  reviewCompleted?: boolean;
  lineItems: { label: string; amountUsd: number }[];
  imageUrl: string | null;
  stage: VendorStage;
  productionReleased: boolean;
  installationConfirmed: string | null;
  proposedStart: string | null;
  proposedEnd: string | null;
  issue: string | null;
  paymentStatus: string;
  notifiedStage?: string | null;
  notifiedAt?: string | null;
  demo?: boolean;
};
const string = (v: unknown, max = 300) =>
  typeof v === "string" ? v.slice(0, max) : "";
export function safeImageUrl(v: unknown): string | null {
  if (typeof v !== "string") return null;
  if (/^\/bliss-basic\/beach-bumz\/[a-z0-9._-]+$/i.test(v)) return v;
  try {
    const u = new URL(v);
    return u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}
/** Deliberate allowlist: no client, payment, retail pricing, margin, notes or documents. */
export function projectVendorOrder(
  row: any,
  vendorTenantId: string,
): VendorOrder | null {
  const po = row.config?.vendorFulfillment;
  if (!po || po.vendorTenantId !== vendorTenantId || po.released !== true)
    return null;
  if ((!Number.isFinite(po.amountUsd) || po.amountUsd < 0) && po.wholesalePending !== true) return null;
  return {
    id: row.id,
    number: string(row.order_id),
    version: row.updated_at,
    title: string(po.title),
    layout: string(po.layout),
    upper: string(po.upper),
    lower: string(po.lower),
    access: string(po.access),
    finish: string(po.finish),
    finishSpecifications: Array.isArray(po.finishSpecifications)?po.finishSpecifications.filter((v:any)=>Number.isInteger(v.bunk)&&v.bunk>0).map((v:any)=>({room:string(v.room,80),bunk:v.bunk,finish:string(v.finish,80),paintCode:/^SW\d{4}$/.test(v.paintCode||'')?v.paintCode:null})):
      (Array.isArray(po.scope)?po.scope:[]).filter((line:unknown)=>typeof line==='string'&&/Custom Color|Gloss White|Matte White/.test(line)).map((line:string,i:number)=>({room:'Purchased scope',bunk:Number(line.match(/bunk (\d+)/i)?.[1]||i+1),finish:line.includes('Custom Color')?'Custom Color':line.includes('Matte White')?'Matte White':'Gloss White',paintCode:line.match(/\bSW\d{4}\b/)?.[0]??null})),
    quantity:
      Number.isInteger(po.quantity) && po.quantity > 0 ? po.quantity : 1,
    zip: /^\d{5}$/.test(po.zip) ? po.zip : "",
    amountUsd: Number.isFinite(po.amountUsd) ? po.amountUsd : 0,
    wholesalePending: po.wholesalePending === true,
    scope: Array.isArray(po.scope) ? po.scope.filter((s:unknown) => typeof s === "string").map((s:string) => string(s,600)) : [],
    reviewRequired: po.reviewRequired === true,
    reviewCompleted: po.reviewCompleted === true,
    lineItems: Array.isArray(po.lineItems)
      ? po.lineItems
          .filter(
            (i: any) =>
              typeof i.label === "string" && Number.isFinite(i.amountUsd),
          )
          .map((i: any) => ({ label: string(i.label), amountUsd: i.amountUsd }))
      : [],
    imageUrl: safeImageUrl(po.imageUrl),
    stage: VENDOR_STAGES.includes(po.stage) ? po.stage : "review",
    productionReleased: po.productionReleased === true,
    installationConfirmed: string(po.installationConfirmed, 10) || null,
    proposedStart: string(po.proposedStart, 10) || null,
    proposedEnd: string(po.proposedEnd, 10) || null,
    issue: string(po.issue, 2000) || null,
    paymentStatus: string(po.paymentStatus, 80) || "Not scheduled",
    notifiedStage: string(po.notifiedStage, 30) || null,
    notifiedAt: string(po.notifiedAt, 40) || null,
  };
}
export function vendorActionPatch(
  order: VendorOrder,
  body: any,
  today: string,
) {
  if (body.action === "issue") {
    if (
      typeof body.issue !== "string" ||
      !body.issue.trim() ||
      body.issue.length > 2000
    )
      throw new Error("Describe the scope issue (up to 2,000 characters).");
    return { issue: body.issue.trim() };
  }
  if (body.action === "accept") {
    if (order.wholesalePending) throw new Error("Bliss must confirm the wholesale amount before acceptance.");
    if (!["review", "accepted"].includes(order.stage))
      throw new Error("This order has already entered production.");
    const validDate = (v: unknown): v is string =>
      typeof v === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(v) &&
      !Number.isNaN(Date.parse(v)) &&
      new Date(v).toISOString().slice(0, 10) === v;
    if (
      !validDate(body.start) ||
      !validDate(body.end) ||
      body.start < today ||
      body.end < body.start
    )
      throw new Error("Choose a valid future installation window.");
    return {
      stage: "accepted",
      proposedStart: body.start,
      proposedEnd: body.end,
    };
  }
  if (body.action === "advance") {
    if (order.issue)
      throw new Error(
        "Bliss must resolve the scope issue before work progresses.",
      );
    if (order.reviewRequired && !order.reviewCompleted) throw new Error("The purchased Room Review must be completed before production.");
    if (!order.productionReleased)
      throw new Error(
        "Awaiting approved dimensions and production release from Bliss.",
      );
    const next = {
      accepted: "fabricating",
      fabricating: "painting",
      painting: "installing",
      installing: "complete",
    }[order.stage];
    if (!next) throw new Error("Accept the order before starting work.");
    if (next === "installing" && !order.installationConfirmed)
      throw new Error("Bliss must confirm the installation date first.");
    return { stage: next };
  }
  throw new Error("Unsupported action.");
}
export const DEMO_VENDOR_ORDER: VendorOrder = {
  id: "demo",
  number: "BB-1042",
  version: "",
  title: "Twin XL over Queen",
  layout: "Offset layout",
  upper: "Twin XL",
  lower: "Queen",
  access: "Short-end ladder",
  finish: "Painted white",
  quantity: 1,
  zip: "32459",
  amountUsd: 3750,
  lineItems: [
    { label: "Twin XL upper", amountUsd: 1750 },
    { label: "Queen lower", amountUsd: 2000 },
  ],
  imageUrl: "/bliss-basic/beach-bumz/twin-xl-over-queen.jpeg",
  stage: "review",
  productionReleased: false,
  installationConfirmed: null,
  proposedStart: null,
  proposedEnd: null,
  issue: null,
  paymentStatus: "Not scheduled",
  demo: true,
};


